import {
  GET_CART,
  ADD_TO_CART,
  DELETE_FROM_CART,
  CART_LOADING,
  GET_ITEMS_CART,
  ADD_ITEM_TO_CART,
  DELETE_ITEM_FROM_CART,
  CART_ITEM_LOADING,
} from "../types/cartTypes";

import axios from "axios";

const jsonConfig = { headers: { "Content-Type": "application/json" } };

// De-dupe concurrent requests per id
const cartInFlight = new Set();
const itemsInFlight = new Set();

/** Get cart for userId (idempotent). */
export const getCart = (userId) => async (dispatch) => {
  if (!userId) return { ok: false, error: "Missing user id" };
  if (cartInFlight.has(userId)) return { ok: true, cached: true };

  cartInFlight.add(userId);
  try {
    dispatch({ type: CART_LOADING, meta: { userId } });
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}api/cart/${userId}/`,
      jsonConfig
    );
    dispatch({ type: GET_CART, payload: res.data, meta: { userId } });
    return { ok: true, data: res.data };
  } catch (err) {
    const msg = err?.response?.data || "Failed to load cart";
    return { ok: false, error: msg };
  } finally {
    cartInFlight.delete(userId);
  }
};

/** Increment cart quantity for userId. */
export const addToCart = (userId, quantity) => async (dispatch) => {
  if (!userId) return { ok: false, error: "Missing user id" };
  const current = typeof quantity === "number" ? quantity : 0;
  const nextQty = Math.max(0, current + 1);

  try {
    dispatch({ type: CART_LOADING, meta: { userId } });
    const res = await axios.put(
      `${process.env.REACT_APP_API_URL}api/cart/${userId}/`,
      { userId, quantity: nextQty },
      jsonConfig
    );
    dispatch({ type: ADD_TO_CART, payload: res.data, meta: { userId } });
    return { ok: true, data: res.data };
  } catch (err) {
    const msg = err?.response?.data || "Failed to add to cart";
    return { ok: false, error: msg };
  }
};

/** Decrement cart quantity for userId. */
export const deleteFromCart = (userId, quantity) => async (dispatch) => {
  if (!userId) return { ok: false, error: "Missing user id" };
  const current = typeof quantity === "number" ? quantity : 0;
  const nextQty = Math.max(0, current - 1);

  try {
    dispatch({ type: CART_LOADING, meta: { userId } });
    const res = await axios.put(
      `${process.env.REACT_APP_API_URL}api/cart/${userId}/`,
      { userId, quantity: nextQty },
      jsonConfig
    );
    dispatch({ type: DELETE_FROM_CART, payload: res.data, meta: { userId } });
    return { ok: true, data: res.data };
  } catch (err) {
    const msg = err?.response?.data || "Failed to remove from cart";
    return { ok: false, error: msg };
  }
};

/** Get items by cartId (idempotent). */
export const getItemsCart = (cartId) => async (dispatch) => {
  if (!cartId) return { ok: false, error: "Missing cart id" };
  if (itemsInFlight.has(cartId)) return { ok: true, cached: true };

  itemsInFlight.add(cartId);
  try {
    dispatch({ type: CART_ITEM_LOADING, meta: { cartId } });
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}api/showItemsCart/${cartId}/`,
      jsonConfig
    );
    dispatch({ type: GET_ITEMS_CART, payload: res.data, meta: { cartId } });
    return { ok: true, data: res.data };
  } catch (err) {
    const msg = err?.response?.data || "Failed to load cart items";
    return { ok: false, error: msg };
  } finally {
    itemsInFlight.delete(cartId);
  }
};

/** Add an item (or bump quantity if existing). */
export const addItemToCart = (cartId, productId, quantity) => async (dispatch) => {
  if (!cartId || !productId) return { ok: false, error: "Missing cartId or productId" };
  const qty = !quantity || quantity < 1 ? 1 : quantity;

  try {
    const detectRes = await axios.get(
      `${process.env.REACT_APP_API_URL}api/cartItemDetectSameItem/${cartId}/${productId}/`,
      jsonConfig
    );

    if (!Array.isArray(detectRes.data) || detectRes.data.length === 0) {
      const createRes = await axios.post(
        `${process.env.REACT_APP_API_URL}api/cartItem/`,
        { cartId, productId, quantity: qty },
        jsonConfig
      );
      dispatch({ type: ADD_ITEM_TO_CART, payload: createRes.data, meta: { cartId, productId } });
      return { ok: true, data: createRes.data };
    } else {
      const current = detectRes.data[0];
      const updateRes = await axios.put(
        `${process.env.REACT_APP_API_URL}api/cartItem/id/${current?.id}/`,
        { cartId, productId, quantity: (current?.quantity || 0) + qty },
        jsonConfig
      );
      dispatch({ type: ADD_ITEM_TO_CART, payload: updateRes.data, meta: { cartId, productId } });
      return { ok: true, data: updateRes.data };
    }
  } catch (err) {
    const msg = err?.response?.data || "Failed to add item to cart";
    return { ok: false, error: msg };
  }
};

/** Delete a cart item by cartItemId. */
export const deleteItemFromCart = (itemId) => async (dispatch) => {
  if (!itemId) return { ok: false, error: "Missing item id" };
  try {
    await axios.delete(
      `${process.env.REACT_APP_API_URL}api/cartItem/id/${itemId}/`,
      jsonConfig
    );
    dispatch({ type: DELETE_ITEM_FROM_CART, meta: { itemId } });
    return { ok: true };
  } catch (err) {
    const msg = err?.response?.data || "Failed to delete cart item";
    return { ok: false, error: msg };
  }
};
